import { alpha, createTheme } from '@mui/material/styles';

/**
 * The form redesign restyles every input the same way — 48px controls, a label that is
 * always shrunk into the notch. Doing that once as a theme nested *inside* the form beats
 * repeating `sx` on six field components, and unlike editing the global theme it cannot
 * leak into the search bars, filters and dialogs that share MUI's inputs.
 *
 * It is derived from the outer theme rather than built from scratch, so the Hyundai and
 * Mitsubishi palettes keep their own `primary.main` — the orange in the design mock is
 * this site's `primary.main`, not a literal.
 *
 * Everything MUI already does by itself is left alone: the 12px radius is
 * `shape.borderRadius`, the focus ring is `primary.main`, the error state is `error.main`,
 * and the hover border is `text.primary`. Only the four things MUI does differently from
 * the design are set here.
 */

export const FIELD_HEIGHT = 48;

/**
 * MUI's resting outlined-input border. It has no palette token of its own (`divider` is a
 * lighter 0.12), so the controls that are not inputs but must sit flush with them — the
 * option cards, the checkbox square — borrow it from here rather than repeating the value.
 */
export const FIELD_BORDER_COLOR = alpha('#000', 0.23);

export default function createFormTheme(outerTheme) {
  return createTheme(outerTheme, {
    palette: {
      // The global palette sets `text.secondary` to solid black and `text.disabled` to a
      // near-white `#F4F4F4`, so neither reads as "quieter than the answer next to it".
      // Restoring MUI's own two tones here — and only here — lets its components render the
      // design's greys without a single colour override at the call site.
      text: {
        secondary: alpha(outerTheme.palette.common.black, 0.6),
        disabled: alpha(outerTheme.palette.common.black, 0.38),
      },
    },
    components: {
      MuiOutlinedInput: {
        // The label is always shrunk (see MuiInputLabel below), so the outline has to be
        // permanently notched — MUI derives the two from the same form-control state, but
        // from separate props, and a shrunk label over an unnotched outline sits on the line.
        defaultProps: { notched: true },
        styleOverrides: {
          root: {
            '&:not(.MuiInputBase-multiline)': {
              height: FIELD_HEIGHT,
            },
            '&.MuiInputBase-multiline': {
              padding: '14px 15px',
            },
            // The only reason these appear at all: the global theme pins every notched
            // outline to `2px !important`, so a lighter resting state cannot win without
            // matching that, and focus/error have to claim their 2px back.
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1.5px !important',
            },
            [`&.Mui-focused .MuiOutlinedInput-notchedOutline,
              &.Mui-error .MuiOutlinedInput-notchedOutline`]: {
              borderWidth: '2px !important',
            },
            '&.Mui-disabled': {
              backgroundColor: outerTheme.palette.quaternary.main,
            },
          },
          input: {
            height: '100%',
            boxSizing: 'border-box',
            '&.MuiInputBase-inputMultiline': {
              height: 'auto',
              padding: 0,
            },
          },
        },
      },
      MuiInputLabel: {
        // Change 2 of the redesign in one line: no floating, no jump between placeholder
        // and notch optics.
        defaultProps: { shrink: true },
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              // The dot in the mock — decorative, so it stays out of the accessible name.
              '&::before': {
                content: '""',
                flex: 'none',
                display: 'block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: outerTheme.palette.error.main,
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          // Needed so the value sits centred once the control is a fixed 48px tall.
          select: {
            display: 'flex',
            alignItems: 'center',
            minHeight: 'unset',
          },
        },
      },
    },
  });
}
